import { useState } from 'react'
import {Mail, Phone, Instagram, Send, Check} from 'lucide-react'
import PageHero from '@/components/PageHero'
import SEO from '@/components/SEO'
import { contact, links } from '@/content/site'

type Audience = 'particulier' | 'institution'

function Contact() {
  const [audience, setAudience] = useState<Audience>('particulier')
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', org: '', message: '' })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name || !form.email || !form.message) return
    setSubmitted(true)
  }

  return (
    <div>
      <SEO
        title="Contact — HeartCanvas | Réserver un appel gratuit"
        description="Prenons un moment ensemble. Réservez un appel gratuit de 20 minutes avec Alexandra."
        path="/contact"
      />
      <PageHero
        eyebrow={contact.hero.eyebrow}
        title={contact.hero.title}
        intro={contact.hero.intro}
      />

      <section className="px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
          {/* INFOS */}
          <aside className="md:col-span-4 md:sticky md:top-32 md:self-start space-y-10">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{contact.direct.eyebrow}</div>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <Mail size={18} className="text-[#9E3A5A] mt-1 shrink-0" />
                  <a href={`mailto:${links.email}`} className="font-serif-display text-2xl text-[#1F1B1D] link-underline">
                    {links.email}
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={18} className="text-[#9E3A5A] mt-1 shrink-0" />
                  <a href={`tel:${links.phone.replace(/\s/g, '')}`} className="font-serif-display text-2xl text-[#1F1B1D] link-underline">
                    {links.phone}
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <Instagram size={18} className="text-[#9E3A5A] mt-1 shrink-0" />
                  <a
                    href={links.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="font-serif-display text-2xl text-[#1F1B1D] link-underline"
                  >
                    {contact.direct.instagramHandle}
                  </a>
                </li>
              </ul>
            </div>

            <div className="border-t border-[#DCC9D2] pt-10">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-4">{contact.doublePorte.eyebrow}</div>
              <p className="text-[15px] leading-[1.75] text-[#3A3236]">
                <span className="text-[#1F1B1D]">{contact.doublePorte.particuliers}</span> {contact.doublePorte.particuliersText}<br />
                <span className="text-[#1F1B1D] mt-2 block">{contact.doublePorte.institutions}</span> {contact.doublePorte.institutionsText}
              </p>
            </div>
          </aside>

          {/* FORMULAIRE */}
          <div className="md:col-span-8">
            <div className="flex gap-2 mb-10">
              {[
                { k: 'particulier' as const, l: contact.toggle.particulier },
                { k: 'institution' as const, l: contact.toggle.institution },
              ].map(t => (
                <button
                  type="button"
                  key={t.k}
                  onClick={() => setAudience(t.k)}
                  className={`flex-1 sm:flex-none px-6 py-3 text-[12px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
                    audience === t.k
                      ? 'bg-[#9E3A5A] text-[#F6EFF4] border-[#9E3A5A]'
                      : 'bg-transparent text-[#3A3236] border-[#DCC9D2] hover:border-[#1F1B1D]'
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>

            {submitted ? (
              <div className="border border-[#DCC9D2] bg-[#EFE5EC] p-12 md:p-16 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#9E3A5A] flex items-center justify-center mb-6">
                  <Check size={24} className="text-[#F6EFF4]" />
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-[#1F1B1D] mb-4">{contact.success.title}</h3>
                <p className="text-[16px] text-[#3A3236] leading-relaxed max-w-md mx-auto">
                  {contact.success.body} <span className="text-[#1F1B1D]">{form.email}</span>.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-7">
                <div className="grid sm:grid-cols-2 gap-7">
                  <Field label={contact.fields.name} required>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full bg-transparent border-b border-[#1F1B1D] py-3 text-[16px] focus:outline-none focus:border-[#9E3A5A] transition-colors"
                    />
                  </Field>
                  <Field label={contact.fields.email} required>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full bg-transparent border-b border-[#1F1B1D] py-3 text-[16px] focus:outline-none focus:border-[#9E3A5A] transition-colors"
                    />
                  </Field>
                </div>

                {audience === 'institution' && (
                  <Field label={contact.fields.org}>
                    <input
                      type="text"
                      value={form.org}
                      onChange={e => setForm(f => ({ ...f, org: e.target.value }))}
                      className="w-full bg-transparent border-b border-[#1F1B1D] py-3 text-[16px] focus:outline-none focus:border-[#9E3A5A] transition-colors"
                    />
                  </Field>
                )}

                <Field
                  label={audience === 'particulier' ? contact.fields.messageParticulier : contact.fields.messageInstitution}
                  required
                >
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full bg-transparent border-b border-[#1F1B1D] py-3 text-[16px] focus:outline-none focus:border-[#9E3A5A] transition-colors resize-none"
                  />
                </Field>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-[#9E3A5A] text-[#F6EFF4] text-[12px] tracking-[0.25em] uppercase hover:bg-[#7A2D46] transition-colors duration-500"
                  >
                    {audience === 'particulier' ? contact.buttons.particulier : contact.buttons.institution}
                    <Send size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </button>
                  <p className="mt-6 text-[12px] text-[#3A3236]/70 max-w-md">
                    {contact.privacyNote}
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.25em] uppercase text-[#C0923F]">
        {label}{required && <span className="text-[#9E3A5A]"> *</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  )
}

export default Contact
